/** PIN unlock overlay: shown on launch when a PIN is configured (no auto-lock timer by decision). */
import { useState } from 'react';
import { useApp } from '../state/AppState';
import { passkeyProvider } from '../lib/pin';
import { Icon } from './Icon';

export function PinLock() {
  const { unlock, resetAll } = useApp();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [passkeyNote, setPasskeyNote] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (busy || pin.length === 0) return;
    setBusy(true);
    setError(null);
    const result = await unlock(pin);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'PIN 不正确');
      setPin('');
    }
  };

  const tryPasskey = async (): Promise<void> => {
    const supported = await passkeyProvider.supported();
    if (!supported) {
      setPasskeyNote('当前浏览器不支持 Passkey');
      return;
    }
    const result = await passkeyProvider.authenticate('解锁 Luminis');
    setPasskeyNote(result.error ?? 'Passkey 已取消');
  };

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 bg-[#070b16] px-8">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-500/15 text-indigo-300 ring-1 ring-indigo-400/30">
        <Icon name="lock" className="h-8 w-8" />
      </div>
      <div className="text-center">
        <h1 className="text-lg font-semibold">Luminis 已锁定</h1>
        <p className="mt-1 text-xs text-slate-400">输入 PIN 解锁（高危审批仍需再次验证）</p>
      </div>
      <form
        className="flex w-full max-w-xs flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <input
          value={pin}
          onChange={(event) => setPin(event.target.value.replace(/[^0-9]/gu, '').slice(0, 8))}
          inputMode="numeric"
          type="password"
          autoFocus
          placeholder="••••"
          aria-label="PIN 解锁"
          className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-xl tracking-[0.5em] outline-none focus:border-indigo-400/60"
        />
        {error ? <p className="text-center text-xs text-rose-300">{error}</p> : null}
        <button
          type="submit"
          disabled={busy || pin.length === 0}
          className="rounded-xl bg-indigo-500 py-3 text-sm font-semibold text-white disabled:opacity-40"
        >
          {busy ? '验证中…' : '解锁'}
        </button>
      </form>
      <div className="flex flex-col items-center gap-2 text-xs text-slate-500">
        <button
          type="button"
          className="underline underline-offset-2"
          onClick={() => void tryPasskey()}
        >
          使用 Passkey（预留接口）
        </button>
        {passkeyNote ? <span className="text-slate-400">{passkeyNote}</span> : null}
        {confirmReset ? (
          <span className="flex items-center gap-2">
            <span className="text-rose-300">清除全部本地数据？</span>
            <button type="button" className="text-rose-300 underline" onClick={() => resetAll()}>
              确认清除
            </button>
            <button type="button" className="underline" onClick={() => setConfirmReset(false)}>
              取消
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => setConfirmReset(true)}
          >
            忘记 PIN？清除本地数据并重新配对
          </button>
        )}
      </div>
    </div>
  );
}
